import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  getPrescription,
  removePrescription,
  updatePrescription,
  viewPrescriptionById,
} from "../API/PrescriptionApi";
import { Toast } from "bootstrap";
import { getServiceDetails } from "../API/LinkedServicesApi";
import NotesTextField from "../Worklist/NotesTextField";
import {
  Button,
  Card,
  Grid2,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TableCell,
  Table,
  TableRow,
  TableHead,
  TableBody,
  Autocomplete,
  duration,
} from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import PrescriptionView from "./PrescriptionView"; // Import the PrescriptionView component
import { formatAge, showLoadingImage } from "../Utility/Utility";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import MenuBar from "../common/MenuBar";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";

import { IconButton } from "@mui/material";
import {
  MedicineDurationOptions,
  PrescriptionTypes,
  SelectableComorbidities,
  TestNames,
} from "../Utility/Constants";
import { MedicineList } from "../Utility/MedicineList";
import GenericTextField from "../common/GenericTextField";

function LaparoscopyPrescriptionForm({
  serviceId,
  prescriptionId,
  onRemovePrescription,
  onSavePrescription,
}) {
  const { auth } = useContext(AuthContext);
  const [prescriptionStatus, setPrescriptionStatus] = useState();
  const [currentService, setCurrentService] = useState();
  const [medicines, setMedicines] = useState([]);
  const [tests, setTests] = useState([]);
  const [complaint, setComplaint] = useState();
  const [diagnosis, setDiagnosis] = useState();
  const [additionalNotes, setAdditionalNotes] = useState();
  const [followupDate, setFollowupDate] = useState();
  const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();
  const [currentPrescription, setCurrentPrescription] = useState();
  const { showSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [comorbiditiesNotes, setComorbiditiesNotes] = useState("");
  const [medicalAndSurgicalHistory, setMedicalAndSurgicalHistory] =
    useState("");
  const [comorbiditiesConditions, setComorbiditiesConditions] = useState(
    SelectableComorbidities,
  );

  // Theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  const isPrescriptionEmpty = () => {
    // Check if all critical fields are empty
    const hasComplaint = complaint && complaint.trim().length > 0;
    const hasDiagnosis = diagnosis && diagnosis.trim().length > 0;
    const hasComorbidities =
      comorbiditiesConditions?.some((c) => c.selected) ||
      (comorbiditiesNotes && comorbiditiesNotes.trim().length > 0);
    const hasMedicalHistory =
      medicalAndSurgicalHistory && medicalAndSurgicalHistory.trim().length > 0;
    const hasMedicines = medicines && medicines.length > 0;
    const hasTests = tests && tests.length > 0;
    const hasAdditionalNotes =
      additionalNotes && additionalNotes.trim().length > 0;
    const hasFollowupDate = followupDate && followupDate.trim().length > 0;

    // Return true if ALL fields are empty
    return (
      !hasComplaint &&
      !hasDiagnosis &&
      !hasComorbidities &&
      !hasMedicalHistory &&
      !hasMedicines &&
      !hasTests &&
      !hasAdditionalNotes &&
      !hasFollowupDate
    );
  };

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          var result = await getServiceDetails(auth, serviceId);
          if (result.isSuccess) {
            setCurrentService(result?.data);
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error",
          );
        } finally {
          // Only set loading to false if we're not also loading the prescription
          if (prescriptionId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadPrescription = async () => {
      if (auth != null && prescriptionId != null) {
        setIsSaving(true);
        try {
          var result = await getPrescription(auth, prescriptionId);
          if (result.isSuccess) {
            var prescription = result?.data;
            setPrescriptionToBeEdited(prescription);
            setPrescriptionStatus(prescription?.status);
            var detailedPrescriptionJson = JSON.parse(
              prescription?.detailedPrescription,
            );
            if (prescription?.detailedPrescription) {
              setComplaint(detailedPrescriptionJson?.complaint);
              setDiagnosis(detailedPrescriptionJson?.diagnosis);
              setComorbiditiesConditions(
                detailedPrescriptionJson?.comorbidities,
              );
              setComorbiditiesNotes(
                detailedPrescriptionJson?.comorbiditiesNotes,
              );
              setMedicalAndSurgicalHistory(
                detailedPrescriptionJson?.medicalAndSurgicalHistory,
              );
              setMedicines(detailedPrescriptionJson?.medicines);
              setTests(detailedPrescriptionJson?.tests);
              setAdditionalNotes(detailedPrescriptionJson?.additionalNotes);
              setFollowupDate(detailedPrescriptionJson?.followupDate);
            } else {
              setComplaint("");
              setDiagnosis("");
              setComorbiditiesConditions(SelectableComorbidities);
              setComorbiditiesNotes("");
              setMedicalAndSurgicalHistory("");
              setMedicines([]);
              setTests([]);
              setAdditionalNotes("");
              setFollowupDate("");
            }
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };

    if (prescriptionId) {
      loadPrescription();
      loadService();
    } else {
      loadService();
    }
  }, [auth, serviceId, prescriptionId, setCurrentPrescription]);

  const savePrescription = async (status) => {
    setIsSaving(true);
    try {
      var prescription = {};

      prescription.complaint = complaint;
      prescription.diagnosis = diagnosis;
      prescription.comorbidities = comorbiditiesConditions;
      prescription.comorbiditiesNotes = comorbiditiesNotes;
      prescription.medicalAndSurgicalHistory = medicalAndSurgicalHistory;
      prescription.medicines = medicines;
      prescription.tests = tests;
      prescription.additionalNotes = additionalNotes;
      prescription.followupDate = followupDate;
      prescription.status = status;
      var result = {};
      if (!prescriptionToBeEdited) {
        result = await addPrescription(
          auth,
          prescription,
          currentService?.id,
          PrescriptionTypes.GeneralPrescription,
          status,
        );
      } else {
        result = await updatePrescription(
          auth,
          prescription,
          prescriptionToBeEdited?.id,
          status,
        );
      }
      if (result.isSuccess) {
        onSavePrescription(serviceId, prescriptionId, prescription.status);
        showSnackbar(
          "The prescription has been saved successfully.",
          "success",
        );
      } else {
        showSnackbar(result?.data, "error");
      }
    } catch (error) {
      showSnackbar("Error saving prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const onRemovePrescriptionClick = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        var result = await removePrescription(auth, prescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, prescriptionId);
        } else {
          showSnackbar(result?.data, "error");
        }
      }
    } catch (error) {
      showSnackbar("Error removing prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };
  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode +" "+ serviceName;

  const viewPrescription = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        await viewPrescriptionById(auth, prescriptionId);
      }
    } catch (error) {
      showSnackbar("Error viewing prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Improved MedicineCard component with better text field handling
  const MedicineCard = ({ med }) => {
    return (
      <Card className="outerCard" variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            #{med.serialNo}
          </Typography>
          <IconButton
            color="error"
            size="small"
            onClick={() => {
              const filteredMeds = medicines?.filter(
                (m) => m.serialNo !== med.serialNo,
              );
              const nextMeds = filteredMeds.map((m, i) => ({
                ...m,
                serialNo: i + 1,
              }));
              setMedicines(nextMeds);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Autocomplete
          freeSolo
          options={MedicineList?.map((m) => m.name)}
          value={med.name || ""}
          onChange={(_, newValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo ? { ...m, name: newValue } : m,
            );
            setMedicines(updatedMedicines);
          }}
          onInputChange={(_, newInputValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo ? { ...m, name: newInputValue } : m,
            );
            setMedicines(updatedMedicines);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Medicine"
              fullWidth
              size="small"
              margin="normal"
              variant="outlined"
              sx={{ mb: 1 }}
              placeholder="Medicine"
            />
          )}
        />

        <Autocomplete
          freeSolo
          options={MedicineDurationOptions}
          value={med.duration || ""}
          onChange={(_, newValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo ? { ...m, duration: newValue } : m,
            );
            setMedicines(updatedMedicines);
          }}
          onInputChange={(_, newInputValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo
                ? { ...m, duration: newInputValue }
                : m,
            );
            setMedicines(updatedMedicines);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Duration"
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                background: "#f9f9f9",
                borderRadius: 1,
              }}
              placeholder="Duration"
            />
          )}
        />

        <Autocomplete
          freeSolo
          options={["1-0-0", "1-0-1", "1-1-1", "0-0-1"]}
          value={med.frequency || ""}
          onChange={(_, newValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo ? { ...m, frequency: newValue } : m,
            );
            setMedicines(updatedMedicines);
          }}
          onInputChange={(_, newInputValue) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo
                ? { ...m, frequency: newInputValue }
                : m,
            );
            setMedicines(updatedMedicines);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Frequency"
              fullWidth
              size="small"
              variant="outlined"
              sx={{ background: "#f9f9f9", borderRadius: 1 }}
              placeholder="Frequency"
            />
          )}
        />

        <TextField
          label="Instructions"
          fullWidth
          size="small"
          margin="normal"
          variant="outlined"
          multiline
          rows={2}
          value={med.instructions || ""}
          onChange={(e) => {
            const updatedMedicines = medicines.map((m) =>
              m.serialNo === med.serialNo
                ? { ...m, instructions: e.target.value }
                : m,
            );
            setMedicines(updatedMedicines);
          }}
        />
      </Card>
    );
  };

  // Improved TestCard component with better text field handling
  const TestCard = ({ test }) => {
    return (
      <Card className="outerCard" variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            #{test.serialNo}
          </Typography>
          <IconButton
            color="error"
            size="small"
            onClick={() => {
              const filteredTests = tests?.filter(
                (t) => t.serialNo !== test.serialNo,
              );
              const nextTests = filteredTests.map((t, i) => ({
                ...t,
                serialNo: i + 1,
              }));
              setTests(nextTests);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <TextField
          label="Test Name"
          fullWidth
          size="small"
          margin="normal"
          variant="outlined"
          value={test.name || ""}
          onChange={(e) => {
            const updatedTests = tests.map((t) =>
              t.serialNo === test.serialNo ? { ...t, name: e.target.value } : t,
            );
            setTests(updatedTests);
          }}
          sx={{ mb: 1 }}
          InputProps={{
            style: { fontSize: "0.875rem" }, // Better mobile readability
          }}
        />

        <TextField
          label="Test Date"
          fullWidth
          size="small"
          margin="normal"
          variant="outlined"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={test.toBeDoneOn || ""}
          onChange={(e) => {
            const updatedTests = tests.map((t) =>
              t.serialNo === test.serialNo
                ? { ...t, toBeDoneOn: e.target.value }
                : t,
            );
            setTests(updatedTests);
          }}
          sx={{ mb: 1 }}
        />

        <TextField
          label="Instructions"
          fullWidth
          size="small"
          margin="normal"
          variant="outlined"
          multiline
          rows={2}
          value={test.instructions || ""}
          onChange={(e) => {
            const updatedTests = tests.map((t) =>
              t.serialNo === test.serialNo
                ? { ...t, instructions: e.target.value }
                : t,
            );
            setTests(updatedTests);
          }}
        />
      </Card>
    );
  };

  // Function to render form for editable mode
  const renderEditableForm = () => {
    return (
      <Card
        className="outerCard"
        variant="outlined"
        sx={{
          width: "100%",
          p: { xs: 2, md: 4 },

          borderRadius: 3,
          opacity: isSaving ? 0.7 : 1,
          transition: "opacity 0.3s ease",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          position: "relative",
        }}
      >
        <Grid2 size={12}>
          <Grid2 container size={12}>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Patient Name"
                value={
                  currentService?.patientPrefix +
                  " " +
                  currentService?.patientFirstName +
                  " " +
                  currentService?.patientLastName
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Age and Gender"
                value={
                  formatAge(currentService?.patientAgeInYears) +
                  " and " +
                  currentService?.patientGender
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="MRN"
                value={displayText(currentService?.patientMRN)}
              />
            </Grid2>
          </Grid2>
          <Grid2 container size={12}>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Visit Id"
                value={displayText(currentService?.visitDisplayId)}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Visit Type"
                value={displayText(currentService?.visitType)}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Visit Date Time"
                value={convertUTCToTimeZoneDateTimeString(
                  new Date().toISOString(),
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                )}
              />
            </Grid2>
          </Grid2>
          <Grid2 container size={12}>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Service Id"
                value={displayText(currentService?.displayId)}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Service Name"
                value={finalserviceName}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <HeaderValueDisplay
                header="Service Date Time"
                value={convertUTCToTimeZoneDateTimeString(
                  new Date().toISOString(),
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                )}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        {/* Patient's Complaint Section */}
        <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Chief Complaints
          </Typography>
          <NotesTextField
            value={complaint || ""}
            onChange={(newValue) => setComplaint(newValue)}
            fullWidth
            multiline
            rows={3}
            placeholder="Describe the patient's complaint"
            // value={complaint || ""}
            // onChange={(e) => setComplaint(e.target.value)}
            variant="outlined"
            sx={{ background: "#fff", borderRadius: 2 }}
          />
        </Box>

        {/* Comorbidities */}
        {/* Comorbidities */}
        <Box mb={3}>
          <Typography fontWeight={"bold"}>Comorbidities</Typography>
          <Autocomplete
            multiple
            options={comorbiditiesConditions}
            getOptionLabel={(option) => option.displayValue}
            value={comorbiditiesConditions?.filter((c) => c.selected)}
            onChange={(_, selectedOptions) => {
              const updated = comorbiditiesConditions.map((c) => ({
                ...c,
                selected: selectedOptions.some((sel) => sel.value === c.value),
              }));
              setComorbiditiesConditions(updated);
            }}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: "4px 8px", minHeight: "auto" }}>
                <Checkbox
                  checked={selected}
                  size="small"
                  style={{ marginRight: 4, padding: 2 }}
                />
                <span style={{ fontSize: "0.875rem" }}>
                  {option.displayValue}
                </span>
              </li>
            )}
            renderInput={(params) => (
              <GenericTextField
                {...params}
                label="Comorbidities"
                placeholder="Select comorbidities"
                variant="outlined"
                size="small"
                sx={{ background: "#fff", borderRadius: 2, mt: 1 }}
              />
            )}
            ListboxProps={{
              sx: {
                maxHeight: 300,
                "& .MuiAutocomplete-option": {
                  minHeight: "auto",
                  padding: "4px 8px",
                },
              },
            }}
          />

          <NotesTextField
            value={comorbiditiesNotes || ""}
            onChange={(newValue) => setComorbiditiesNotes(newValue)}
            label="Additional notes on comorbidities"
            disabled={isSaving || prescriptionStatus === "Final"}
            rows={2}
            noteStatus={prescriptionStatus}
          />
        </Box>

        {/* Medical & Surgical History */}
        <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Medical & Surgical History
          </Typography>
          <NotesTextField
            value={medicalAndSurgicalHistory || ""}
            onChange={(newValue) => setMedicalAndSurgicalHistory(newValue)}
            fullWidth
            multiline
            rows={2}
            placeholder="Describe relevant medical and surgical history"
            // value={medicalAndSurgicalHistory || ""}
            // onChange={(e) => setMedicalAndSurgicalHistory(e.target.value)}
            variant="outlined"
            sx={{ background: "#fff", borderRadius: 2 }}
          />
        </Box>

        {/* Diagnosis */}
        {/* <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Diagnosis Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Diagnosis details"
            value={diagnosis || ""}
            onChange={(e) => setDiagnosis(e.target.value)}
            variant="outlined"
            sx={{ background: "#fff", borderRadius: 2 }}
          />
        </Box> */}
        <Box mb={3}>
          <Typography fontWeight="bold">Diagnosis Notes</Typography>

          <NotesTextField
            value={diagnosis || ""}
            onChange={(newValue) => setDiagnosis(newValue)}
            label="Enter diagnosis details"
            disabled={isSaving || prescriptionStatus === "Final"}
            rows={4}
            noteStatus={prescriptionStatus}
          />
        </Box>

        {/* Medicines Section */}
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="end">
            <Typography fontWeight="bold">Medicines</Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                setMedicines([
                  ...medicines,
                  { serialNo: medicines?.length + 1 },
                ]);
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Medicine
            </Button>
          </Box>

          {/* Responsive display for medicines */}
          {isMobile ? (
            // Mobile view with cards
            <Box>
              {medicines &&
                medicines.map((med) => (
                  <MedicineCard key={med.serialNo} med={med} />
                ))}
            </Box>
          ) : (
            // Desktop view with table
            // <Box sx={{ overflowX: "auto" }}>
            //   <table className="table" id="medicineList">
            //     <thead>
            //       <tr>
            //         <th scope="col" className="col-1">
            //           #
            //         </th>
            //         <th scope="col" className="col-3">
            //           Medicine
            //         </th>
            //         <th scope="col" className="col-2">
            //           Duration
            //         </th>
            //         <th scope="col" className="col-2">
            //           Frequency
            //         </th>
            //         <th scope="col" className="col-3">
            //           Instructions
            //         </th>
            //         <th scope="col" className="col-1">
            //           Actions
            //         </th>
            //       </tr>
            //     </thead>
            //     <tbody>
            //       {medicines &&
            //         medicines.map((med) => (
            //           <tr key={med.serialNo}>
            //             <th scope="row">{med.serialNo}</th>
            //             <td>
            //               <input
            //                 className="w-100"
            //                 type="text"
            //                 value={med.name || ""}
            //                 onChange={(e) => {
            //                   var copiedMedicines = [...medicines];
            //                   var medIndexToBeUpdated =
            //                     copiedMedicines.findIndex(
            //                       (m) => m.serialNo === med.serialNo
            //                     );
            //                   copiedMedicines[medIndexToBeUpdated].name =
            //                     e.target.value;
            //                   setMedicines(copiedMedicines);
            //                 }}
            //               />
            //             </td>
            //             <td>
            //               <input
            //                 className="w-100"
            //                 type="text"
            //                 value={med.duration || ""}
            //                 onChange={(e) => {
            //                   var copiedMedicines = [...medicines];
            //                   var medIndexToBeUpdated =
            //                     copiedMedicines.findIndex(
            //                       (m) => m.serialNo === med.serialNo
            //                     );
            //                   copiedMedicines[medIndexToBeUpdated].duration =
            //                     e.target.value;
            //                   setMedicines(copiedMedicines);
            //                 }}
            //               />
            //             </td>
            //             <td>
            //               <input
            //                 className="w-100"
            //                 type="text"
            //                 value={med.frequency || ""}
            //                 onChange={(e) => {
            //                   var copiedMedicines = [...medicines];
            //                   var medIndexToBeUpdated =
            //                     copiedMedicines.findIndex(
            //                       (m) => m.serialNo === med.serialNo
            //                     );
            //                   copiedMedicines[medIndexToBeUpdated].frequency =
            //                     e.target.value;
            //                   setMedicines(copiedMedicines);
            //                 }}
            //               />
            //             </td>
            //             <td>
            //               <textarea
            //                 className="w-100"
            //                 rows={2}
            //                 type="text"
            //                 value={med.instructions || ""}
            //                 onChange={(e) => {
            //                   var copiedMedicines = [...medicines];
            //                   var medIndexToBeUpdated =
            //                     copiedMedicines.findIndex(
            //                       (m) => m.serialNo === med.serialNo
            //                     );
            //                   copiedMedicines[
            //                     medIndexToBeUpdated
            //                   ].instructions = e.target.value;
            //                   setMedicines(copiedMedicines);
            //                 }}
            //               />
            //             </td>
            //             <td>
            //               <button
            //                 className="btn btn-outline-danger small p-1 ms-1"
            //                 onClick={() => {
            //                   const filteredMeds = medicines.filter(
            //                     (m) => m.serialNo !== med.serialNo
            //                   );
            //                   var count = 0;
            //                   const nextMeds = filteredMeds.map((m) => {
            //                     return { ...m, serialNo: ++count };
            //                   });
            //                   setMedicines(nextMeds);
            //                 }}
            //               >
            //                 <i className="bi bi-x-lg"></i>
            //               </button>
            //             </td>
            //           </tr>
            //         ))}
            //     </tbody>
            //   </table>
            // </Box>
            <Box sx={{ overflowX: "auto", mt: 2 }}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{ boxShadow: 2, p: 2, background: "#fff" }}
              >
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", width: "5%" }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                        Medicine
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                        Duration
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                        Frequency
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                        % Instructions
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", width: "5%" }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicines &&
                      medicines.map((med) => (
                        <TableRow key={med.serialNo} hover>
                          <TableCell align="center">{med.serialNo}</TableCell>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              options={MedicineList?.map(
                                (m) => `${m.name} ${m.strength}`,
                              )}
                              value={med.name || ""}
                              onChange={(_, newValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, name: newValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              onInputChange={(_, newInputValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, name: newInputValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Medicine"
                                  fullWidth
                                  size="small"
                                  margin="normal"
                                  variant="outlined"
                                  sx={{ mb: 1 }}
                                  placeholder="Medicine"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              options={MedicineDurationOptions}
                              value={med.duration || ""}
                              onChange={(_, newValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, duration: newValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              onInputChange={(_, newInputValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, duration: newInputValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Duration"
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    background: "#f9f9f9",
                                    borderRadius: 1,
                                  }}
                                  placeholder="Duration"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            {/* <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={med.frequency || ""}
                              onChange={(e) => {
                                const copied = [...medicines];
                                const idx = copied.findIndex(
                                  (m) => m.serialNo === med.serialNo
                                );
                                copied[idx].frequency = e.target.value;
                                setMedicines(copied);
                              }}
                              sx={{ background: "#f9f9f9", borderRadius: 1 }}
                              placeholder="Frequency"
                            /> */}
                            <Autocomplete
                              freeSolo
                              options={["1-0-0", "1-0-1", "1-1-1", "0-0-1"]}
                              value={med.frequency || ""}
                              onChange={(_, newValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, frequency: newValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              onInputChange={(_, newInputValue) => {
                                const updatedMedicines = medicines.map((m) =>
                                  m.serialNo === med.serialNo
                                    ? { ...m, frequency: newInputValue }
                                    : m,
                                );
                                setMedicines(updatedMedicines);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Frequency"
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    background: "#f9f9f9",
                                    borderRadius: 1,
                                  }}
                                  placeholder="Frequency"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              value={med.instructions || ""}
                              onChange={(e) => {
                                const copied = [...medicines];
                                const idx = copied.findIndex(
                                  (m) => m.serialNo === med.serialNo,
                                );
                                copied[idx].instructions = e.target.value;
                                setMedicines(copied);
                              }}
                              sx={{ background: "#f9f9f9", borderRadius: 1 }}
                              placeholder="Instructions"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                const filtered = medicines?.filter(
                                  (m) => m.serialNo !== med.serialNo,
                                );
                                let count = 0;
                                const nextMeds = filtered.map((m) => ({
                                  ...m,
                                  serialNo: ++count,
                                }));
                                setMedicines(nextMeds);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
        </Box>

        {/* Recommended Tests Section */}
        <Box mt={3} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="end">
            <Typography fontWeight="bold">Recommended Tests</Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                setTests([...tests, { serialNo: tests?.length + 1 }]);
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Test
            </Button>
          </Box>

          {/* Responsive display for tests */}
          {isMobile ? (
            // Mobile view with cards
            <Box>
              {tests &&
                tests.map((test) => (
                  <TestCard key={test.serialNo} test={test} />
                ))}
            </Box>
          ) : (
            // Desktop view with table
            <Box sx={{ overflowX: "auto", mt: 2 }}>
              <Card
                variant="outlined"
                sx={{ borderRadius: 3, p: 2, background: "#fff" }}
              >
                <Table sx={{ minWidth: 50 }} size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", width: "5%" }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                        Test Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                        Tentative Date of Test
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                        Instructions
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "bold", width: "5%" }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tests &&
                      tests.map((test) => (
                        <TableRow key={test.serialNo} hover>
                          <TableCell align="center">{test.serialNo}</TableCell>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              options={TestNames}
                              value={test.name || ""}
                              onChange={(_, newValue) => {
                                const updatedTests = tests.map((t) =>
                                  t.serialNo === test.serialNo
                                    ? { ...t, name: newValue }
                                    : t,
                                );
                                setTests(updatedTests);
                              }}
                              onInputChange={(_, newInputValue) => {
                                const updatedTests = tests.map((t) =>
                                  t.serialNo === test.serialNo
                                    ? { ...t, name: newInputValue }
                                    : t,
                                );
                                setTests(updatedTests);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Test Name"
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    background: "#f9f9f9",
                                    borderRadius: 1,
                                  }}
                                  placeholder="Test Name"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <GenericDatePicker
                              label="Tentative Date"
                              value={test.toBeDoneOn || ""}
                              onChange={(formattedDate) => {
                                const updatedTests = tests.map((t) =>
                                  t.serialNo === test.serialNo
                                    ? { ...t, toBeDoneOn: formattedDate }
                                    : t,
                                );
                                setTests(updatedTests);
                              }}
                              displayFormat="DD-MMM-YYYY" // 3 letter month
                              outputFormat="DD-MMM-YYYY" // Output format
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  size: "small",
                                  fullWidth: true,
                                  margin: "none",
                                  placeholder: "DD-MMM-YYYY", // 3 letter placeholder
                                  InputProps: {
                                    placeholder: "DD-MMM-YYYY",
                                  },
                                  sx: {
                                    background: "#f9f9f9",
                                    borderRadius: 1,
                                    "& .MuiOutlinedInput-root": {
                                      height: "40px",
                                      fontSize: "0.875rem",
                                    },
                                  },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              value={test.instructions || ""}
                              onChange={(e) => {
                                const copied = [...tests];
                                const idx = copied.findIndex(
                                  (t) => t.serialNo === test.serialNo,
                                );
                                copied[idx].instructions = e.target.value;
                                setTests(copied);
                              }}
                              sx={{ background: "#f9f9f9", borderRadius: 1 }}
                              placeholder="Instructions"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                const filtered = tests?.filter(
                                  (t) => t.serialNo !== test.serialNo,
                                );
                                let count = 0;
                                const nextTests = filtered.map((t) => ({
                                  ...t,
                                  serialNo: ++count,
                                }));
                                setTests(nextTests);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
        </Box>

        {/* Additional Notes */}
        {/* <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Additional Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={additionalNotes || ""}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            variant="outlined"
            sx={{ background: "#fff", borderRadius: 2 }}
          />
        </Box> */}
        <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Additional Notes
          </Typography>
          <Box sx={{ position: "relative" }}>
            <NotesTextField
              value={additionalNotes || ""}
              onChange={(newValue) => setAdditionalNotes(newValue)}
              label="Enter additional notes "
              disabled={isSaving || prescriptionStatus === "Final"}
              rows={3}
              noteStatus={prescriptionStatus}
            />
          </Box>
        </Box>

        {/* Follow Up Date */}
        <Box mb={3}>
          <Typography fontWeight="bold" gutterBottom>
            Follow Up Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GenericDatePicker
              label="Follow Up Date"
              value={followupDate}
              onChange={(formattedDate) => setFollowupDate(formattedDate)}
            />
          </LocalizationProvider>
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loading Overlay */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, // One level above Dialog
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <MenuBar
        showDelete={prescriptionStatus === "Draft"}
        onDelete={onRemovePrescriptionClick}
        showDownload={prescriptionStatus === "Final"}
        onDownload={viewPrescription}
        showSave={prescriptionStatus !== "Final"}
        onSave={() => savePrescription("Draft")}
        saveLabel="Save"
        saveTooltip="Save Prescription as Draft"
        saveDisabled={isPrescriptionEmpty()} // Add this line
        showFinalize={prescriptionStatus !== "Final"}
        onFinalize={() => savePrescription("Final")}
        finalizeDisabled={isPrescriptionEmpty()} // Add this line
      />
      <Grid2 container size={12} sx={{ marginX: "10px", marginY: "10px" }}>
        <Grid2 container size={12}></Grid2>

        <Grid2
          container
          size={12}
          sx={{
            overflow: "auto", // Enable scrolling when content overflows
            maxHeight: "80vh", // Adjust as needed for your layout
          }}
        >
          <Grid2
            size={{ xs: 12, sm: 12, md: 12, lg: 8, xl: 8 }}
            sx={{ paddingTop: "10px" }}
          >
            {prescriptionStatus === "Final" ? (
              // Use the PrescriptionView component for read-only mode
              <Box
                sx={{
                  opacity: isSaving ? 0.7 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                <PrescriptionView
                  currentService={currentService}
                  complaint={complaint}
                  comorbiditiesNotes={comorbiditiesNotes}
                  comorbiditiesConditions={comorbiditiesConditions}
                  medicalAndSurgicalHistory={medicalAndSurgicalHistory}
                  diagnosis={diagnosis}
                  medicines={medicines}
                  tests={tests}
                  additionalNotes={additionalNotes}
                  followupDate={followupDate}
                />
              </Box>
            ) : (
              // Use the editable form for draft mode
              renderEditableForm()
            )}
          </Grid2>
          <Grid2 size={3}></Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default LaparoscopyPrescriptionForm;
